import { ApplicationRef, Inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable } from 'rxjs';

import { nullish, precondition } from 'shared-utils/data';

import { I18nService } from '../../i18n';
import { SnackbarActionHelper, SnackbarColorHelper, SnackbarIconHelper } from '../helpers';
import { Snackbar, SnackbarActionConfiguration, SnackbarConfiguration, SnackbarType } from '../models';
import { SNACKBAR_DEFAULT_EXPIRATION, SNACKBAR_DEFAULT_EXPIRATION_MS } from '../snackbar.token';
import { SnackbarRef } from './snackbar-ref';

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  /**
   * Observable of the snackbar visibility value
   * @type {Observable<boolean>}
   */
  public readonly visibility$: Observable<boolean>;

  // NOTE: needs to be a BehaviourSubject to tick with an initial value
  private readonly _visibility$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _defaultExpirationTime = SNACKBAR_DEFAULT_EXPIRATION_MS;

  private _snackbarRef: SnackbarRef | undefined;
  private _snackbar: SnackbarType | undefined;
  private _expirationTimeoutHandle: ReturnType<typeof setTimeout> | undefined;

  /**
   * Current snackbar in readonly
   * @returns a readonly version of internal snackbar
   */
  public get snackbar(): Snackbar | undefined {
    return this._snackbar;
  }

  constructor(
    private readonly _applicationRef: ApplicationRef,
    private readonly _i18nService: I18nService,
    @Inject(SNACKBAR_DEFAULT_EXPIRATION) _snackbarDefaultExpiration$: Observable<number | undefined>,
  ) {
    this.visibility$ = this._visibility$.asObservable();

    // NOTE: Since this service is root-scoped, no unsubscription is needed for this subscription as it will last the entire application lifecycle
    _snackbarDefaultExpiration$
      .pipe(filter(expiration => expiration !== undefined))
      .subscribe(defaultExpiration => this._defaultExpirationTime = defaultExpiration);
  }

  /**
   * Open the snackbar with the configuration in parameter
   * @param {SnackbarConfiguration} configuration - configuration of the snackbar
   * @param {ThemePalette} configuration.color - The color of snackbar
   * @param {SnackbarText} configuration.message - Message to display in snackbar.
   * - Note: It can be an observable to support translations.
   * @param {number} [configuration.expiration] - Time in millis for the snackbar to be visible.
   * - Note: Set to falsy to disable the expiration
   * @param {PrimaryConfiguration} [configuration.primaryConfiguration] - Extra configuration for primary color.
   * @param {ActionsConfiguration} [configuration.actions] - Configuration for actions in snackbar.
   * - Note: Unavailable for primary color, configuration will be ignored
   *
   * @returns {SnackbarRef} a ref to access to the snackbar and its methods
   */
  public open(configuration: SnackbarConfiguration): SnackbarRef {
    SnackbarColorHelper.checkUnsupportedColor(configuration.color);

    this._snackbar = {
      color: configuration.color,
      message: this.getTranslationStream(configuration.message, configuration.messageParams),
      icon: SnackbarIconHelper.getIconConfiguration(configuration),
      actions: SnackbarActionHelper.getActions(this.translateActionsConfiguration(configuration)),
      subMessage: this.getSubMessage(configuration),
      isPrimary: SnackbarColorHelper.isPrimary(configuration),
    };

    this._snackbarRef = new SnackbarRef(this._snackbar, this._visibility$);
    this._visibility$.next(true);

    this.tryToDisappear(configuration);

    return this._snackbarRef;
  }

  private getSubMessage(configuration: SnackbarConfiguration): string | Observable<string> | undefined {
    if (!SnackbarColorHelper.isPrimary(configuration) || nullish(configuration.primaryConfiguration?.subMessage)) {
      return undefined;
    }

    return this.getTranslationStream(configuration.primaryConfiguration.subMessage);
  }

  private tryToDisappear(configuration: SnackbarConfiguration): void {
    // NOTE: clear timeout of previous snackbar
    clearTimeout(this._expirationTimeoutHandle);

    const hasExpirationDefinedInConfiguration: boolean = 'expiration' in configuration;
    const shouldNotExpire: boolean = hasExpirationDefinedInConfiguration && !configuration.expiration;

    if (!shouldNotExpire) {
      const expiration = configuration.expiration ?? this._defaultExpirationTime;

      this._expirationTimeoutHandle = setTimeout(() => {
        precondition(this._snackbarRef, 'SnackbarRef must be defined before closing it 😮');

        this._snackbarRef.close(configuration.afterClosedMessage, configuration.onAfterClosed);
        this._applicationRef.tick();
      }, expiration);
    }
  }

  private translateActionsConfiguration(configuration: SnackbarConfiguration): SnackbarConfiguration {
    const translatedSnackbar: SnackbarConfiguration = {
      ...configuration,
    };

    if (configuration.actions?.left) {
      translatedSnackbar.actions = {
        ...translatedSnackbar.actions,
        left: this.getTranslatedAction(configuration.actions.left),
      };
    }

    if (configuration.actions?.right) {
      translatedSnackbar.actions = {
        ...translatedSnackbar.actions,
        right: this.getTranslatedAction(configuration.actions.right),
      };
    }

    return translatedSnackbar;
  }

  private getTranslatedAction(action: SnackbarActionConfiguration): SnackbarActionConfiguration {
    return {
      ...action,
      text: action.text
        ? this.getTranslationStream(action.text)
        : undefined,
    };
  }

  private getTranslationStream(message$: string | Observable<string>, translationParams?: { [key: string]: string }): Observable<string> {
    return typeof message$ === 'string'
      ? this._i18nService.addTranslationStream(message$, translationParams)
      : message$;
  }
}
